// TripOverviewCard — at-a-glance summary card for the chronological timeline.
//
// Renders at the top of the TimelineView, above all phase containers. Replicates
// the CloudScape `Container` + `Header` + `KeyValuePairs` visual using our
// existing `designTokens` and the sibling `Badge` / `KeyValuePairs` primitives.
//
// Structure (per design's TripOverviewCard section):
//   - Header titled "Trip overview" with a description noting that all times are
//     shown in local time zones (Req 1.3).
//   - A trip-status Badge (In progress / Upcoming / Completed) and a trip-type
//     Badge (Round trip / One way) (Req 1.4, 1.5).
//   - A KeyValuePairs grid (columns={isMobile ? 1 : 4}) of the 8 summary fields:
//     traveler, destination, departure date, return date, outbound flight,
//     return flight, hotel, and trip status (Req 1.2, 10.3).
//
// Consumes the `overview` object produced by buildOverview.
//
// Validates: Requirements 1.2, 1.3, 1.4, 1.5, 10.3

import React from 'react';

import {
  colors,
  typography,
  fontWeights,
  spacing,
  cardStyles,
} from '../../../styles/designTokens';
import { Badge } from './Badge';
import { KeyValuePairs } from './KeyValuePairs';

/**
 * Header description text — explicitly notes that all times shown across the
 * timeline are in their local time zones (Req 1.3).
 */
const HEADER_DESCRIPTION =
  'Review your full trip at a glance. All times are shown in local time zones.';

/**
 * @typedef {Object} OverviewBadge
 * @property {'blue'|'green'|'red'|'grey'} color
 * @property {string} label
 *
 * @typedef {Object} TripOverview
 * @property {string} traveler
 * @property {string} destination
 * @property {string} departureDate
 * @property {string} returnDate
 * @property {string} outboundFlight
 * @property {string} returnFlight
 * @property {string} hotel
 * @property {OverviewBadge} statusBadge
 * @property {OverviewBadge} typeBadge
 */

/**
 * @typedef {Object} TripOverviewCardProps
 * @property {TripOverview} overview - Summary fields plus status/type badges.
 * @property {boolean} [isMobile]    - Collapses the grid to a single column.
 */

/**
 * TripOverviewCard renders the trip summary card.
 *
 * @param {TripOverviewCardProps} props
 * @returns {React.ReactElement|null}
 */
export function TripOverviewCard({ overview, isMobile = false }) {
  if (!overview || typeof overview !== 'object') {
    return null;
  }

  const {
    traveler,
    destination,
    departureDate,
    returnDate,
    outboundFlight,
    returnFlight,
    hotel,
    statusBadge,
    typeBadge,
  } = overview;

  // The 8 summary fields: 7 data fields + trip status (rendered as a Badge node
  // so it reads consistently with the header status badge).
  const items = [
    { label: 'Traveler', value: traveler },
    { label: 'Destination', value: destination },
    { label: 'Departure date', value: departureDate },
    { label: 'Return date', value: returnDate },
    { label: 'Outbound flight', value: outboundFlight },
    { label: 'Return flight', value: returnFlight },
    { label: 'Hotel', value: hotel },
    {
      label: 'Trip status',
      value: statusBadge ? (
        <Badge color={statusBadge.color}>{statusBadge.label}</Badge>
      ) : null,
    },
  ];

  return (
    <div data-testid="trip-overview-card" style={cardStyles.container}>
      <div
        style={{
          ...cardStyles.header,
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          rowGap: spacing.elementGap,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
            minWidth: 0,
          }}
        >
          <span style={cardStyles.headerText}>Trip overview</span>
          <span
            style={{
              fontSize: typography.caption.fontSize,
              fontWeight: fontWeights.regular,
              color: colors.cardHeaderText,
              opacity: 0.8,
            }}
          >
            {HEADER_DESCRIPTION}
          </span>
        </div>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: spacing.elementGap,
            flexShrink: 0,
          }}
        >
          {statusBadge ? (
            <Badge color={statusBadge.color}>{statusBadge.label}</Badge>
          ) : null}
          {typeBadge ? (
            <Badge color={typeBadge.color}>{typeBadge.label}</Badge>
          ) : null}
        </div>
      </div>
      <div style={cardStyles.body}>
        <KeyValuePairs columns={isMobile ? 1 : 4} items={items} isMobile={isMobile} />
      </div>
    </div>
  );
}

export default TripOverviewCard;
